/**
 * CheerEval Color Palette
 * Extracted from Cheerocracy MVP - April 2026
 *
 * This file centralizes all color values used across the application
 * to ensure consistency and make theme changes easier.
 *
 * CSS cannot import this file: `src/styles/colorPalette.css` defines
 * `var(--color-*)` only for colors currently used in `.css` files. When you
 * use a new token in CSS, add it there and keep values aligned with this object.
 */

export const colors = {
  // Brand accents
  brand: {
    accentPink: '#FF6B9D',
    accentTeal: '#4ECDC4',
    lightPink: '#fbe9e3',
  },

  // Primary Colors (CheerEval Dark Red)
  primary: {
    main: '#800000',        // Primary dark red - used for buttons, links, accents
    light: '#ffd6d6',       // Light red background
    lighter: '#fff5f5',     // Very light red background
    dark: '#660000',        // Darker red for hover states
    darker: '#4d0000',      // Even darker red for active states
    withAlpha: 'rgba(128, 0, 0, 0.25)', // Primary with transparency
  },

  // Success Colors (Green)
  success: {
    main: '#4caf50',        // Success green
    light: '#e8f5e8',       // Light green background
    dark: '#2e7d32',        // Dark green text
    withAlpha: 'rgba(76, 175, 80, 0.7)', // Green with transparency
  },

  // Error/Danger Colors (Red)
  error: {
    main: '#f44336',        // Error red
    light: '#ffebee',       // Light red background
    dark: '#d32f2f',        // Dark red text
    darker: '#c62828',      // Even darker red
    ios: '#ff3b30',         // iOS red
    iosHover: '#e02e24',    // iOS red hover
    iosActive: '#c0271f',   // iOS red active
    withAlpha: 'rgba(244, 67, 54, 0.7)', // Red with transparency
    focusAlpha: 'rgba(211, 47, 47, 0.25)', // Invalid input focus ring (align with colorPalette.css)
  },

  // Info / Tip (Pale Yellow callouts)
  info: {
    light: '#fff8e1',       // Pale yellow background
    medium: '#fbe5a0',      // Border / accent
    main: '#c97c00',        // Amber (icon)
    dark: '#5b4100',        // Dark amber (text)
  },

  // iOS Blue (Alternative Primary)
  ios: {
    blue: '#007aff',        // iOS blue
    blueHover: '#0062cc',   // iOS blue hover
    blueActive: '#0051a8',  // iOS blue active
    blueLight: '#7cb1ff',   // iOS blue light (disabled)
  },

  // Neutral/Gray Scale
  neutral: {
    black: '#000000',
    white: '#ffffff',
    lightest: '#f9f9f9',
    lighter: '#f8f9fa',
    light: '#f5f5f5',
    medium: '#f0f0f0',
    gray: '#e0e0e0',
    mediumGray: '#ddd',
    darkGray: '#bdbdbd',
    darker: '#757575',
    dark: '#666',
    darker2: '#555',
    darkest: '#424242',
    text: '#333',
    offwhite_background: '#fff8ed',
  },

  // Background Colors
  background: {
    main: '#f8f9fa',        // Main app background
    card: '#ffffff',        // Card background
    light: '#f5f5f5',       // Light background
    medium: '#f0f0f0',      // Medium background
    dark: '#1a2f4a',        // Dark background (floor layout)
    modal: 'rgba(0, 0, 0, 0.9)', // Modal backdrop
    modalLight: 'rgba(0, 0, 0, 0.2)', // Light modal backdrop
    /** Align with `colorPalette.css` `--color-overlay-immersive` (photo lightbox). */
    immersiveOverlay: 'rgba(0, 0, 0, 0.92)',
    cameraScrim: 'rgba(0, 0, 0, 0.45)',
    cameraIconBtn: 'rgba(0, 0, 0, 0.35)',
  },

  // Border Colors
  border: {
    light: '#eee',
    medium: '#e0e0e0',
    dark: '#ddd',
  },

  // Shadow Colors
  shadow: {
    light: 'rgba(0, 0, 0, 0.05)',
    medium: 'rgba(0, 0, 0, 0.1)',
    dark: 'rgba(0, 0, 0, 0.15)',
    darker: 'rgba(0, 0, 0, 0.22)',
    darkest: 'rgba(0, 0, 0, 0.3)',
    modal: 'rgba(0, 0, 0, 0.5)',
  },

  // Focus/Outline Colors (input rings use primary maroon; see colorPalette.css)
  focus: {
    ring: '#800000',
    ringAlpha: 'rgba(128, 0, 0, 0.25)',
    iosBlue: 'rgba(0, 122, 255, 0.3)',
  },

  // Text Colors
  text: {
    primary: '#333',
    secondary: '#555',
    tertiary: '#666',
    muted: '#757575',
    light: '#aaa',
    lighter: '#ccc',
    white: '#ffffff',
    whiteAlpha: 'rgba(255, 255, 255, 0.42)',
  },

  // Legacy/Bootstrap Colors (from Login component)
  legacy: {
    danger: '#dc3545',
    dangerBg: '#f8d7da',
    dangerBorder: '#f5c6cb',
    success: '#28a745',
    successBg: '#d4edda',
    successBorder: '#c3e6cb',
  },

  // Skeleton Loading
  skeleton: {
    base: '#f6f7f8',
    highlight: '#edeef1',
    gradient: 'linear-gradient(to right, #f6f7f8 0%, #edeef1 20%, #f6f7f8 40%, #f6f7f8 100%)',
  },
} as const;

// Type for accessing colors
export type ColorPalette = typeof colors;

// Helper function to get color with custom alpha
export const withAlpha = (color: string, alpha: number): string => {
  // Convert hex to rgba
  const r = parseInt(color.slice(1, 3), 16);
  const g = parseInt(color.slice(3, 5), 16);
  const b = parseInt(color.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};
